import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { configApi } from '../services/api';
import { GOOGLE_APP_INTEGRATIONS } from '../lib/constants';

// Hidden for every plan while GOOGLE_APP_INTEGRATIONS is off (lib/constants.js).
const HIDDEN = GOOGLE_APP_INTEGRATIONS ? [] : ['gmail', 'google_calendar'];

// Fallback used until API responds (keeps app functional on first render)
const FALLBACK_LIMITS = {
  free: { teams: false, google_calendar: false, gmail: false, webhooks: false, all_views: false, all_document_types: false },
  basic: { teams: true, google_calendar: true, gmail: false, webhooks: false, all_views: true, all_document_types: true },
  pro: { teams: true, google_calendar: true, gmail: true, webhooks: true, all_views: true, all_document_types: true },
  max: { teams: true, google_calendar: true, gmail: true, webhooks: true, all_views: true, all_document_types: true },
  team: { teams: true, google_calendar: true, gmail: true, webhooks: true, all_views: true, all_document_types: true },
};

let _cachedPlans = null;

// Team has everything Pro has (per member); Max is above both.
const PLAN_RANK = { free: 0, basic: 1, pro: 2, team: 2, max: 3 };

export function useEntitlements() {
  const { user } = useAuth();
  const plan = user?.plan || 'free';
  const [planLimits, setPlanLimits] = useState(_cachedPlans || FALLBACK_LIMITS);

  useEffect(() => {
    if (_cachedPlans) return;
    configApi.getPlans().then(res => {
      _cachedPlans = res.data;
      setPlanLimits(res.data);
    }).catch(() => {});
  }, []);

  const limits = planLimits[plan] || planLimits.free || FALLBACK_LIMITS.free;

  // `has`/`limitOf`/`isAtLeast` are memoized so consumers can pass them to
  // useEffect/useMemo dependency arrays without retriggering on every render.
  // The previous version created fresh function references each render, which
  // turned `useEffect(() => sync(), [has])` in PlannerPage into an infinite
  // loop: every sync response invalidated queries → re-render → new `has` →
  // effect fires again. That spammed POST /google-calendar/sync several
  // dozen times per second and pinned page transitions for ~10s.
  const has = useCallback((feature) => !HIDDEN.includes(feature) && Boolean(limits[feature]), [limits]);
  const limitOf = useCallback((resource) => limits[resource], [limits]);
  const isAtLeast = useCallback((tier) => PLAN_RANK[plan] >= PLAN_RANK[tier], [plan]);

  return useMemo(() => ({
    plan,
    limits,
    has,
    limitOf,
    isAtLeast,
    isPaid: plan !== 'free',
    isPro: (PLAN_RANK[plan] ?? 0) >= PLAN_RANK.pro,
    isBasic: plan === 'basic',
    isFree: plan === 'free',
  }), [plan, limits, has, limitOf, isAtLeast]);
}
